import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { Util } from '@libs/utilities/util';

import { NgTimedAnimationService } from '../../shared/services/timed-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationEffectOptions, INgAnimationStates } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function wobbleToTopRight(translate: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translate = translate ? translate : 8;

	//1 - make transition steps by number of transition state steps
	const difference 	: number = translate / 6; 						// divide the translate value by total number of transition states
	const translate1 	: number = Util.Number.roundDouble(translate);
	const translate2 	: number = Util.Number.roundDouble(translate1 - difference);
	const translate3 	: number = Util.Number.roundDouble(translate2 - difference);
	const translate4 	: number = Util.Number.roundDouble(translate3 - difference);
	const translate5 	: number = Util.Number.roundDouble(translate4 - difference);
	const translate6 	: number = Util.Number.roundDouble(translate5 - difference);
	const translate7 	: number = Util.Number.roundDouble(translate6 - difference);

	//2 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'translate(0)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `translate(${Util.Number.toPositive(translate1)}px, -${Util.Number.toPositive(translate1)}px)`,
				easing 		: 'ease',
				offset 		: 0.083
			}),
			style({
				transform 	: `translate(-${Util.Number.toPositive(translate2)}px, ${Util.Number.toPositive(translate2)}px)`,
				easing 		: 'ease',
				offset 		: 0.165
			}),
			style({
				transform 	: `translate(${Util.Number.toPositive(translate3)}px, -${Util.Number.toPositive(translate3)}px)`,
				easing 		: 'ease',
				offset 		: 0.330
			}),
			style({
				transform 	: `translate(-${Util.Number.toPositive(translate4)}px, ${Util.Number.toPositive(translate4)}px)`,
				easing 		: 'ease',
				offset 		: 0.495
			}),
			style({
				transform 	: `translate(${Util.Number.toPositive(translate5)}px, -${Util.Number.toPositive(translate5)}px)`,
				easing 		: 'ease',
				offset 		: 0.660
			}),
			style({
				transform 	: `translate(-${Util.Number.toPositive(translate6)}px, ${Util.Number.toPositive(translate6)}px)`,
				easing 		: 'ease',
				offset 		: 0.825
			}),
			style({
				transform 	: `translate(${Util.Number.toPositive(translate7)}px, -${Util.Number.toPositive(translate7)}px)`,
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Wobble To Top Right on Event
 */
@Directive({
    selector: '[ngWobbleToTopRightOnEvent]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleToTopRightOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef			  : ElementRef,
		private timedAnimationService : NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translate value is relative to the animation functionality
		if (this.effectOptions?.translate && this.effectOptions?.translate < 1) throw new Error(`WobbleToTopRightOnEventDirective => ngOnInit => FATAL ERROR: Provided translate value should be positive number: ${this.effectOptions?.translate}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleToTopRight(this.effectOptions?.translate),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


/**------------------------------------------------------
 * wobble To Top Right on Mouse Hover
 */
@Directive({
    selector: '[ngWobbleToTopRightOnMouseHover]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgWobbleToTopRightOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translate value is relative to the animation functionality
		if (this.effectOptions?.translate && this.effectOptions?.translate < 1) throw new Error(`WobbleToTopRightOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translate value should be positive number: ${this.effectOptions?.translate}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: wobbleToTopRight(this.effectOptions?.translate),
			event			: EnumNgEventName.MouseHover,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout 	: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomAnimationEffectOptions extends INgAnimationEffectOptions {
	translate : number;
}
